import type { CallCapability, CallPersona, CallState } from '@ndrc/app-sdk/client';
import { callCore, mutateCore } from '../lib/rpc';

/**
 * **L'implémentation de la seconde capacité qu'une app peut demander au cœur : l'appel simulé**
 * (`AppContext.call`, `@ndrc/app-sdk/client` ; ADR-015).
 *
 * Elle vit ICI, dans l'hôte d'application, exactement comme `reviewWork.ts` et pour la même raison :
 * c'est le bureau qui monte une app, c'est donc lui qui lui tend des fonctions déjà refermées sur
 * la session de l'élève connecté. Une app n'appelle jamais `/api/rpc/core/...` elle-même — elle
 * n'en connaît ni l'adresse ni les noms d'opérations.
 *
 * **Rien de ce que l'app envoie ne désigne la cible.** Aucune des cinq opérations du cœur
 * (`call.personas`, `call.current`, `call.start`, `call.say`, `call.hangUp`) n'accepte de
 * `workspaceId`, de `userId`, de `classId` ni même d'identifiant d'appel : le cœur résout l'espace
 * depuis la session (`resolveDefaultWorkspace`) et l'appel visé est toujours le dernier de cet
 * espace. Ce qui traverse ce fichier est donc, au total : un identifiant de personnage choisi dans
 * un catalogue que le cœur a fourni, et le texte que l'élève vient d'écrire.
 *
 * **Ce que ce fichier RETIRE délibérément.** Les opérations du cœur rendent aussi le coût du
 * dernier appel au modèle et l'état du plafond de dépense de la classe (`usage`, `budget`). Ces
 * deux-là ne franchissent pas cette frontière : l'argent est l'affaire de l'enseignant, qui le voit
 * dans son espace (`teacher/aiCost.ts`), jamais celle d'une app ni d'un élève en train de parler à
 * un client. Une capacité ne donne que ce dont son appelant a besoin.
 */

/** Ce que le cœur rend en plus de `CallState` — lu ici, jamais transmis à l'app (voir ci-dessus). */
interface CoreCallState extends CallState {
  workspaceId: string;
  budget: unknown;
  usage: unknown;
}

interface CorePersonas {
  personas: readonly CallPersona[];
  defaultId: string;
}

/** Le contrat de l'app est un sous-ensemble strict de la réponse du cœur : on ne recopie pas champ
 * par champ (une omission passerait inaperçue), on retire ce qui ne doit pas passer. */
function toState(core: CoreCallState): CallState {
  return { call: core.call, unavailable: core.unavailable };
}

/**
 * Ce que l'enseignant lit, et ce qu'il ne fait jamais : **il relit le débrief de son élève, il ne
 * décroche pas à sa place.**
 *
 * `call.current` est la SEULE des cinq à accepter un élève désigné, et seulement d'un enseignant
 * (voir son commentaire dans `packages/core-domain/src/operations/call.ts`). Les trois mutations
 * écriraient dans `core.threads`/`core.messages` au nom de leur acteur : leur ouvrir un élève
 * désigné aurait mis des phrases dans la bouche de quelqu'un. Elles refusent donc ici, avec une
 * phrase — et si l'une passait quand même, le cœur résoudrait l'espace de l'ENSEIGNANT, jamais
 * celui de l'élève : aucune de ces trois opérations ne lit `userId`.
 *
 * `call.personas` reste ouverte : c'est le catalogue de l'entreprise du fichier, pas une donnée
 * d'élève. En observation, l'espace résolu est celui de l'enseignant — même entreprise, même
 * classe, donc même catalogue.
 */
function readOnlyCall(): never {
  throw new Error(
    "Vous lisez le travail d'un élève : vous pouvez relire son appel, pas en passer un à sa " +
      'place. Revenez à votre espace pour décrocher.',
  );
}

export function createCallCapability(
  /** L'élève dont l'enseignant lit le travail, ou `null`. **L'app ne le fournit pas** : ses cinq
   * fonctions n'ont toujours aucun paramètre d'espace, d'élève ni de classe (ADR-015). C'est
   * l'hôte qui referme, ici. */
  observing: { classId: string; userId: string } | null,
  missionRunId?: string,
  sourceAppId?: string,
): CallCapability {
  const scope =
    observing === null
      ? missionRunId
        ? { missionRunId }
        : {}
      : { classId: observing.classId, userId: observing.userId };
  return {
    personas: async () => callCore<CorePersonas>('call.personas', observing === null ? scope : {}),
    current: async () => toState(await callCore<CoreCallState>('call.current', scope)),
    // Les trois mutations dépensent de l'argent : la clé d'idempotence posée par `mutateCore`
    // n'est pas une commodité, c'est ce qui empêche un double clic de payer deux fois — et, pour
    // `call.say`, d'envoyer deux fois la même réplique au client.
    start: async (personaId) =>
      observing !== null
        ? readOnlyCall()
        : toState(
            await mutateCore<CoreCallState>(
              'call.start',
              { ...scope, personaId },
              undefined,
              sourceAppId,
            ),
          ),
    say: async (text) =>
      observing !== null
        ? readOnlyCall()
        : toState(
            await mutateCore<CoreCallState>('call.say', { ...scope, text }, undefined, sourceAppId),
          ),
    hangUp: async () =>
      observing !== null
        ? readOnlyCall()
        : toState(await mutateCore<CoreCallState>('call.hangUp', scope, undefined, sourceAppId)),
  };
}
